import { axios } from 'api'

export type ReturnReason =
  | 'Changed my mind'
  | "Don't need it anymore"
  | 'Ordered by mistake'
  | "We don't have them in stock"
  | 'Exchange'

export type ReturnType = 'Refund' | 'Credit'

export interface ReturnItem {
  orderItemId: string
  quantity: number
  reason: ReturnReason
  returnType: ReturnType
}

export interface CreateReturnParams {
  orderId: string
  items: ReturnItem[]
}

export interface MessageAttachment {
  fileName: string
  fileUrl: string
}

export interface SendMessageParams {
  message: string
  attachments?: MessageAttachment[]
}

export interface ReturnMessage {
  id: string
  returnId: string
  senderType: 'CUSTOMER' | 'ADMIN'
  senderId: string
  message: string
  attachments: MessageAttachment[]
  createdAt: string
  updatedAt: string
  Account: {
    companyName: string
    email: string
    firstName?: string
    lastName?: string
  }
}

export interface Return {
  id: string
  orderId: string
  accountId: string
  status: 'PENDING' | 'UNDER_REVIEW' | 'APPROVED' | 'REJECTED' | 'COMPLETED'
  createdAt: string
  updatedAt: string
  Order: {
    id: string
    netsuiteSalesOrderId: string
    createdAt: string
  }
  ReturnItems: Array<{
    id: string
    returnId: string
    orderItemId: string
    quantity: number
    reason: ReturnReason
    returnType: ReturnType
    OrderItem: {
      id: string
      orderId: string
      productId: string
      quantity: number
      unitPrice: number
      isAssembled: boolean
      hingeDirection: string | null
      Product: {
        sku: string
        color: string
        price: number
        imageIndex: number
        isFixedPrice: boolean
      }
    }
  }>
  ReturnMessages?: ReturnMessage[]
}

interface UploadUrlResponse {
  uploadUrl: string
  fileUrl: string
}

export const createReturn = async (
  params: CreateReturnParams
): Promise<{ success: boolean; error?: string; returnId?: string }> => {
  try {
    const response = await axios.post('/returns', params)

    if (response.status === 201) {
      return {
        success: true,
        returnId: response.data.returnId
      }
    } else {
      return {
        success: false,
        error: response.data.error || 'Failed to create return'
      }
    }
  } catch (error: any) {
    return {
      success: false,
      error: error?.response?.data?.error || 'Failed to create return'
    }
  }
}

export const sendMessage = async (
  returnId: string,
  params: SendMessageParams
): Promise<{ success: boolean; error?: string; messageId?: string }> => {
  try {
    const response = await axios.post(`/returns/${returnId}/messages`, params)

    if (response.status === 201) {
      return {
        success: true,
        messageId: response.data.messageId
      }
    } else {
      return {
        success: false,
        error: response.data.error || 'Failed to send message'
      }
    }
  } catch (error: any) {
    return {
      success: false,
      error: error?.response?.data?.error || 'Failed to send message'
    }
  }
}

export const getMyReturns = async (): Promise<{
  success: boolean
  returns?: Return[]
  error?: string
}> => {
  try {
    const response = await axios.get('/returns')

    if (response.status === 200) {
      return {
        success: true,
        returns: response.data.returns
      }
    } else {
      return {
        success: false,
        error: response.data.error || 'Failed to fetch returns'
      }
    }
  } catch (error: any) {
    return {
      success: false,
      error: error?.response?.data?.error || 'Failed to fetch returns'
    }
  }
}

export const getReturnById = async (
  id: string
): Promise<{
  success: boolean
  return?: Return
  error?: string
}> => {
  try {
    const response = await axios.get(`/returns/${id}`)

    if (response.status === 200) {
      return {
        success: true,
        return: response.data.return
      }
    } else {
      return {
        success: false,
        error: response.data.error || 'Failed to fetch return'
      }
    }
  } catch (error: any) {
    return {
      success: false,
      error: error?.response?.data?.error || 'Failed to fetch return'
    }
  }
}

export const getUploadUrl = async (
  fileName: string,
  fileType: string,
  fileSize: number
): Promise<{
  success: boolean
  data?: UploadUrlResponse
  error?: string
}> => {
  try {
    const response = await axios.get('/returns/files/upload', {
      params: { fileName, fileType, fileSize }
    })

    if (response.status === 200) {
      return {
        success: true,
        data: response.data
      }
    } else {
      return {
        success: false,
        error: response.data.error || 'Failed to get upload URL'
      }
    }
  } catch (error: any) {
    return {
      success: false,
      error: error?.response?.data?.error || 'Failed to get upload URL'
    }
  }
}

export const uploadToS3 = async (
  uploadUrl: string,
  file: File
): Promise<{ success: boolean; error?: string }> => {
  try {
    const response = await fetch(uploadUrl, {
      method: 'PUT',
      body: file,
      headers: {
        'Content-Type': file.type
      }
    })

    if (response.ok) {
      return { success: true }
    } else {
      return {
        success: false,
        error: `S3 upload failed with status ${response.status}`
      }
    }
  } catch (error: any) {
    return {
      success: false,
      error: error.message || 'Failed to upload to S3'
    }
  }
}

export const uploadFile = async (
  file: File
): Promise<{ success: boolean; fileUrl?: string; error?: string }> => {
  try {
    const uploadUrlResult = await getUploadUrl(file.name, file.type, file.size)

    if (!uploadUrlResult.success || !uploadUrlResult.data) {
      return {
        success: false,
        error: uploadUrlResult.error || 'Failed to get upload URL'
      }
    }

    const { uploadUrl, fileUrl } = uploadUrlResult.data

    const uploadResult = await uploadToS3(uploadUrl, file)

    if (!uploadResult.success) {
      return {
        success: false,
        error: uploadResult.error || 'Failed to upload to S3'
      }
    }

    return {
      success: true,
      fileUrl
    }
  } catch (error: any) {
    return {
      success: false,
      error: error.message || 'Failed to upload file'
    }
  }
}
